import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useNavigate } from "react-router";

import { useIsMobile } from "@/hooks/use-mobile";

// --- Context for children to trigger animated back ---
const SlideOverContext = createContext<(() => void) | null>(null);

/** Returns a `handleBack` function that triggers the animated slide-out,
 *  or `null` when rendered outside a SlideOverPage.  */
export function useSlideBack() {
  return useContext(SlideOverContext);
}

// --- Component ---
interface SlideOverPageProps {
  children: React.ReactNode;
  backPath?: string;
}

const CURVE = "cubic-bezier(0.32, 0.72, 0, 1)";

export function SlideOverPage({
  children,
  backPath = "/",
}: SlideOverPageProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Return to the exact previous location when there's an in-app entry to pop
  // to, so closing the slide-over lands the user where they opened it from
  // (e.g. their shelf, or the page a notification was tapped on) rather than
  // always at `backPath` (GOM-99). Falls back to `backPath` for a fresh entry
  // such as a cold-start deep link, where there is no prior page.
  const goBack = useCallback(() => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) {
      navigate(-1);
    } else {
      navigate(backPath, { replace: true });
    }
  }, [navigate, backPath]);

  const containerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const exitingRef = useRef(false);

  // Swipe state (refs to avoid re-renders during gesture)
  const startX = useRef(0);
  const startY = useRef(0);
  const currentX = useRef(0);
  const swiping = useRef(false);
  const directionLocked = useRef(false);
  const startTime = useRef(0);

  // --- Entry animation (all DOM, no state) ---
  useEffect(() => {
    if (!isMobile) return;
    const el = containerRef.current;
    const bd = backdropRef.current;
    if (!el || !bd) return;

    // Single rAF — inline style already positions offscreen, now animate in
    requestAnimationFrame(() => {
      el.style.transition = `transform 0.3s ${CURVE}`;
      el.style.transform = "translateX(0)";
      bd.style.transition = `opacity 0.3s ${CURVE}`;
      bd.style.opacity = "0.2";
    });
  }, [isMobile]);

  // --- Back handler (all DOM, no state) ---
  const handleBack = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;

    if (!isMobile) {
      goBack();
      return;
    }

    const el = containerRef.current;
    const bd = backdropRef.current;
    if (el) {
      el.style.pointerEvents = "none";
      el.style.transition = `transform 0.25s ${CURVE}`;
      el.style.transform = "translateX(100%)";
    }
    if (bd) {
      bd.style.transition = `opacity 0.25s ${CURVE}`;
      bd.style.opacity = "0";
    }
    setTimeout(goBack, 280);
  }, [goBack, isMobile]);

  // Desktop: no slide, just render with a simple fade
  if (!isMobile) {
    return (
      <SlideOverContext.Provider value={handleBack}>
        <div className="animate-fade-in">{children}</div>
      </SlideOverContext.Provider>
    );
  }

  // --- Touch handlers ---
  const onTouchStart = (e: React.TouchEvent) => {
    if (exitingRef.current) return;
    const touch = e.touches[0];
    if (touch.clientX > 30) return;
    startX.current = touch.clientX;
    startY.current = touch.clientY;
    currentX.current = touch.clientX;
    swiping.current = true;
    directionLocked.current = false;
    startTime.current = Date.now();
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!swiping.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - startX.current;
    const dy = touch.clientY - startY.current;

    if (!directionLocked.current && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      directionLocked.current = true;
      if (Math.abs(dy) > Math.abs(dx)) {
        swiping.current = false;
        return;
      }
    }
    if (!directionLocked.current) return;

    const offset = Math.max(0, dx);
    currentX.current = touch.clientX;

    const el = containerRef.current;
    const bd = backdropRef.current;
    if (el) {
      el.style.transition = "none";
      el.style.transform = `translateX(${offset}px)`;
    }
    if (bd) {
      bd.style.transition = "none";
      const progress = 1 - offset / window.innerWidth;
      bd.style.opacity = String(Math.max(0, progress * 0.2));
    }
  };

  const onTouchEnd = () => {
    if (!swiping.current) return;
    swiping.current = false;

    const dx = currentX.current - startX.current;
    const elapsed = Date.now() - startTime.current;
    const velocity = (dx / Math.max(elapsed, 1)) * 1000;
    const screenW = window.innerWidth;

    if (dx > screenW * 0.3 || velocity > 500) {
      // Dismiss
      exitingRef.current = true;
      const el = containerRef.current;
      const bd = backdropRef.current;
      if (el) {
        el.style.pointerEvents = "none";
        el.style.transition = `transform 0.2s ${CURVE}`;
        el.style.transform = `translateX(${screenW}px)`;
      }
      if (bd) {
        bd.style.transition = `opacity 0.2s ${CURVE}`;
        bd.style.opacity = "0";
      }
      setTimeout(goBack, 220);
    } else {
      // Snap back
      const el = containerRef.current;
      const bd = backdropRef.current;
      if (el) {
        el.style.transition = `transform 0.25s ${CURVE}`;
        el.style.transform = "translateX(0)";
      }
      if (bd) {
        bd.style.transition = `opacity 0.25s ${CURVE}`;
        bd.style.opacity = "0.2";
      }
    }
  };

  return (
    <SlideOverContext.Provider value={handleBack}>
      {/* Dim layer behind the sliding panel. Purely decorative, and it must
          stay that way: it shares the panel's z-index and the panel is opaque
          and full-bleed, so the backdrop is only ever *seen* while the panel is
          translated aside (mid-swipe, or during the exit animation) and can
          never be *reached* in normal operation.

          It used to be a clickable "Close" button, which turned into a trap the
          moment anything modal opened on top of the page: Radix sets
          `pointer-events: none` on <body> while a dialog is open, the panel
          inherits that and goes click-through, and this element — with its own
          `pointer-events: auto` — became the one live target left outside the
          dialog. A share-link guest who tapped beside the "Sign in to
          contribute" sheet therefore dismissed the whole page and, having no
          history to pop, landed on /welcome (GOM-263). */}
      <div
        ref={backdropRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-40 bg-black"
        style={{ opacity: 0 }}
      />
      {/* Sliding container — starts offscreen, opaque bg, GPU-accelerated */}
      <div
        ref={containerRef}
        className="bg-background fixed inset-0 z-40"
        style={{ transform: "translateX(100%)", willChange: "transform" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {children}
      </div>
    </SlideOverContext.Provider>
  );
}
